import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  GET_HUB_DAILY_TASK_SUMMARY_AREA_LIST,
  GET_WHS_DAILY_TASK_SUMMARY_AREA_LIST,
  DailyTaskSummaryAreaType,
  DailyTaskSummaryAreaPageRequestType,
  DailyTaskSummaryAreaListData,
  GetHubDailyTaskSummaryAreaListResponse,
  GetWhsDailyTaskSummaryAreaListResponse
} from '../queries/getDailyTaskSummaryAreaList';
import { GET_DSP_DAILY_TASK_SUMMARY_AREA_LIST, GetDspDailyTaskSummaryAreaListResponse } from '../queries/getDspDailyTaskSummaryAreaList';

export type DailyTaskSummaryAreaMode = 'dsp' | 'whs' | 'hub';

type AreaQueryResponse =
  | GetHubDailyTaskSummaryAreaListResponse
  | GetWhsDailyTaskSummaryAreaListResponse
  | GetDspDailyTaskSummaryAreaListResponse;

export const useDailyTaskSummaryAreaQuery = (params: { warehouseId?: string | null; hubId?: number | null; dimensionType?: string; mode: DailyTaskSummaryAreaMode }): {
  summaryData: any | null;
  recordData: DailyTaskSummaryAreaType[];
  calculatedAt: string | null;
  loading: boolean;
  error: any;
  refetch: any;
  handleFilter: (pageRequest: DailyTaskSummaryAreaPageRequestType) => void;
  clearData: () => void;
  currentFilters: DailyTaskSummaryAreaPageRequestType | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
} => {
  const { warehouseId, hubId, dimensionType, mode } = params;
  const isDsp = mode === 'dsp';
  const isHub = mode === 'hub';
  const [pageRequest, setPageRequest] = useState<DailyTaskSummaryAreaPageRequestType | null>({
    taskDate: new Date().toISOString().split('T')[0],
    hubId: hubId ?? undefined,
    warehouseId: warehouseId,
    warehouseIds: undefined,
    dspIds: undefined,
    areaIds: undefined,
    dimensionType: dimensionType,
    pageRequest: {
      page: 1,
      size: 5,
      sortBy: 'id',
      sortDirection: 'ASC'
    }
  });
  const [shouldQuery, setShouldQuery] = useState(false);
  const [queryData, setQueryData] = useState<AreaQueryResponse | null>(null);

  // 当warehouseId/hubId/groupByDsp改变时，更新pageRequest
  useEffect(() => {
    setPageRequest(prev => {
      if (!prev) return null;
      return { ...prev, warehouseId: warehouseId ?? null, hubId: hubId ?? undefined, dimensionType: dimensionType };
    });
  }, [warehouseId, hubId, dimensionType]);

  const selectedQuery = isDsp
    ? GET_DSP_DAILY_TASK_SUMMARY_AREA_LIST
    : isHub
      ? GET_HUB_DAILY_TASK_SUMMARY_AREA_LIST
      : GET_WHS_DAILY_TASK_SUMMARY_AREA_LIST;

  const [executeQuery, { data, loading, error, refetch }] = useLazyQuery<AreaQueryResponse>(
    selectedQuery,
    {
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only', // 每次查询都从网络获取，不使用缓存，避免数据累积
    }
  );

  // 当查询结果更新时，更新本地状态
  useEffect(() => {
    // 无论 data 是否为 null，都更新 queryData
    // 这样可以确保数据状态与查询结果保持一致
    setQueryData(data || null);
  }, [data, loading, error]);

  // 当pageRequest变化时执行查询
  useEffect(() => {
    if (pageRequest && shouldQuery) {
      // 不清空数据，保留旧数据直到新数据到达，避免显示 "No data"
      // 只有在查询成功时才会更新 queryData

      const variables: Record<string, unknown> = {
        taskDate: pageRequest.taskDate,
        dspIds: pageRequest.dspIds,
        areaIds: pageRequest.areaIds,
        cities: pageRequest.cities,
        dimensionType: pageRequest.dimensionType,
        pageRequest: pageRequest.pageRequest
      };
      if (isDsp) {
        // DSP 版本兼容所有可选维度参数
        variables.hubId = pageRequest.hubId;
        variables.warehouseId = pageRequest.warehouseId ? Number(pageRequest.warehouseId) : null;
        variables.warehouseIds = pageRequest.warehouseIds;
      } else if (isHub) {
        // Hub 版本：hubId 必传；可附加 warehouseIds 做子仓筛选
        variables.hubId = pageRequest.hubId;
        variables.warehouseIds = pageRequest.warehouseIds;
      } else {
        // WHS 版本：warehouseId 必传
        variables.warehouseId = pageRequest.warehouseId ? Number(pageRequest.warehouseId) : null;
      }

      executeQuery({ variables });
      setShouldQuery(false);
    }
  }, [pageRequest, shouldQuery, executeQuery, queryData, isDsp, isHub]);

  const handleFilter = (newPageRequest: DailyTaskSummaryAreaPageRequestType) => {
    // 检查是否是切换维度或查询参数发生重大变化
    // 只有当 pageRequest 存在时，才进行比较；如果 pageRequest 为 null，说明是首次查询或切换维度
    const isQueryChanged = !pageRequest || (
      pageRequest.warehouseId !== newPageRequest.warehouseId ||
      pageRequest.hubId !== newPageRequest.hubId ||
      pageRequest.taskDate !== newPageRequest.taskDate ||
      JSON.stringify(pageRequest.dspIds) !== JSON.stringify(newPageRequest.dspIds) ||
      JSON.stringify(pageRequest.areaIds) !== JSON.stringify(newPageRequest.areaIds) ||
      JSON.stringify(pageRequest.cities) !== JSON.stringify(newPageRequest.cities) ||
      JSON.stringify(pageRequest.pageRequest) !== JSON.stringify(newPageRequest.pageRequest)
    );

    // 如果切换了维度或查询参数发生重大变化，清空数据
    // 注意：只有在查询参数真正变化时才清空，避免在数据加载过程中清空
    if (isQueryChanged) {
      // 清空数据，确保显示新数据
      setQueryData(null);
    }

    // 设置新的 pageRequest 和 shouldQuery，确保查询能触发
    setPageRequest(newPageRequest);
    setShouldQuery(true);
  };

  // 清空数据的方法
  const clearData = () => {
    setQueryData(null);
    // 清空 pageRequest，这样 handleFilter 能正确检测到维度切换
    // 注意：warehouseId 的 useEffect 可能会重新设置 pageRequest，但不会设置 shouldQuery
    // 所以需要 handleFilter 来设置 shouldQuery
    setPageRequest(null);
    // 不清空 shouldQuery，因为后续的 handleFilter 会重新设置它
  };

  // 使用本地状态而不是直接使用 data，避免数据累积
  const areaList: DailyTaskSummaryAreaListData | undefined = isDsp
    ? (queryData as GetDspDailyTaskSummaryAreaListResponse | null)?.getDailyTaskSummaryAreaListForDsp
    : isHub
      ? (queryData as GetHubDailyTaskSummaryAreaListResponse | null)?.getHubDailyTaskSummaryAreaList
      : (queryData as GetWhsDailyTaskSummaryAreaListResponse | null)?.getWhsDailyTaskSummaryAreaList;
  const areaSummaryData = areaList?.summaryData || null;
  const recordData = areaList?.recordData || [];
  const calculatedAt = areaList?.calculatedAt || null;


  return {
    summaryData: areaSummaryData,
    recordData,
    calculatedAt,
    loading,
    error,
    refetch,
    handleFilter,
    clearData,
    currentFilters: pageRequest,
    pagination: areaList ? {
      page: areaList.page,
      size: areaList.size,
      totalElements: areaList.totalElements,
      totalPages: areaList.totalPages,
      hasNext: areaList.hasNext,
      hasPrevious: areaList.hasPrevious
    } : null
  };
};
